/**
 * Suggested Responses — inline underlined suggestion links under the last
 * assistant bubble (same placement as questionnaire). Tap sends the choice
 * with a trailing `?` / `？` stripped.
 */

import { useMemo, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { IconArrowBigRightLine } from '@tabler/icons-react'
import { suggestionSendText } from '@/lib/utils/parse-suggestions'
import { cn } from '@/lib/utils'

interface SuggestedResponsesProps {
  suggestions: string[]
  onSend: (suggestion: string) => void
  className?: string
}

export function SuggestedResponses({
  suggestions,
  onSend,
  className
}: SuggestedResponsesProps) {
  const [clickedSuggestion, setClickedSuggestion] = useState<string | null>(null)
  const shouldReduceMotion = useReducedMotion()

  const trimmedSuggestions = useMemo(() => {
    return suggestions.map((s) => s.trim()).filter(Boolean)
  }, [suggestions])

  const handleClick = (suggestion: string) => {
    if (clickedSuggestion) return
    setClickedSuggestion(suggestion)
    const payload = suggestionSendText(suggestion)
    if (!payload) return
    window.setTimeout(
      () => {
        onSend(payload)
      },
      shouldReduceMotion ? 0 : 120
    )
  }

  if (trimmedSuggestions.length === 0) {
    return null
  }

  return (
    <div
      className={cn('mt-3 flex w-full flex-col gap-1.5', className)}
      role="group"
      aria-label="Suggested replies"
    >
      {trimmedSuggestions.map((suggestion, index) => {
        const isClicked = clickedSuggestion === suggestion
        const isDimmed = clickedSuggestion != null && !isClicked
        return (
          <motion.div
            key={`${suggestion}-${index}`}
            initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 4 }}
            animate={{
              opacity: isClicked ? 0.45 : isDimmed ? 0.4 : 1,
              y: shouldReduceMotion ? 0 : isClicked ? -2 : 0
            }}
            transition={{
              delay: shouldReduceMotion || isClicked ? 0 : 0.03 * index,
              duration: shouldReduceMotion ? 0 : 0.18,
              ease: [0.23, 1, 0.32, 1]
            }}
            className="w-full"
          >
            <button
              type="button"
              onClick={() => handleClick(suggestion)}
              disabled={!!clickedSuggestion}
              className={cn(
                'text-foreground group flex max-w-full items-start gap-2 rounded-md text-left text-sm leading-snug',
                'transition-opacity hover:opacity-80 disabled:cursor-default',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'
              )}
            >
              <IconArrowBigRightLine
                className="text-muted-foreground mt-0.5 size-[16px] shrink-0"
                aria-hidden
              />
              <span className="min-w-0 underline decoration-foreground/35 underline-offset-[3px] group-hover:decoration-foreground/60">
                {suggestion}
              </span>
            </button>
          </motion.div>
        )
      })}
    </div>
  )
}
